import { useDataStore } from '@/app/core/components/step-wizard';
import { useProgramWizardContext } from '@/app/core/components/program-wizard/program-wizard-context';
import {
  ProgramWizardMessage,
  ProgramWizardMessageId,
} from '@/app/core/components/program-wizard/types';
import { getClientAttributes } from '@/app/core/components/client-attributes';
import { objectUtils } from '@/app/core/utils';
import { useProgramDependentDataContext } from '@/app/core/components/program-wizard/program-dependent-data-context';
import { Modal } from '@ssar-app/uikit';
import { Box, Button, Flex, Icon, Alert, AlertIcon } from '@chakra-ui/react';
import { AiOutlineInfoCircle } from 'react-icons/ai/index.js';
import { asContact, Contact, OutputLocation } from '@ssar-app/domain';
import { useEffect, useState, useCallback } from 'react';

type DisplayWarningsProps = {
  messages: ProgramWizardMessage[];
};

export const DisplayWarnings = (props: DisplayWarningsProps) => {
  const { messages: messages } = props;
  const { updateWarnings } = useProgramDependentDataContext();
  const { data } = useDataStore();
  const {
    config: { isAutoSaveAllowed },
    metadata: { draftId },
  } = useProgramWizardContext();

  const [deprecatedAttributesWarning, setDeprecatedAttributesWarning] =
    useState<ProgramWizardMessage | undefined>(undefined);
  const [showDeprecatedWarning, setShowDeprecatedWarning] = useState(true);
  const [showNoFileWarning, setShowNoFileWarning] = useState(false);
  const [showFileWarning, setShowFileWarning] = useState(false);
  const [showOutputContactWarning, setShowOutputContactWarning] =
    useState(false);
  const [showOutputContactName, setShowOutputContactName] = useState('');
  const [showAutoSaveWarning, setShowAutoSaveWarning] = useState(false);
  const [showClientContactWarning, setShowClientContactWarning] =
    useState(false);
  const [
    showOutputLocationContactWarning,
    setShowOutputLocationContactWarning,
  ] = useState(false);

  const fetchClientAttributes = useCallback(() => {
    if (
      data?.metadata?.clonedFromProgram &&
      (data?.orderDetails.contact || data?.outputLocations)
    ) {
      getClientAttributes(data?.subCode).then((response) => {
        if (response.resultCode === 0 && response.data) {
          if (data?.orderDetails.contact) {
            const contactIndex = response.data.contacts.findIndex(
              (clientContact) =>
                objectUtils.areEqual(
                  asContact(clientContact),
                  data?.orderDetails?.contact,
                  '_id',
                ),
            );
            setShowClientContactWarning(contactIndex === -1);
          }
          if (data?.outputLocations) {
            const outputContactIndex = response.data.contacts
              .filter(
                (item) =>
                  item.recordType === 'ThirdParty' ||
                  (item.recordType?.toLowerCase() === 'client' &&
                    item.addressType?.toLowerCase() === 'shipping'),
              )
              .findIndex((item) => {
                const toContact = asContact(item);
                return data?.outputLocations.findIndex((outputLoc: Contact) =>
                  objectUtils.areEqual(outputLoc, toContact, '_id'),
                );
              });
            setShowOutputLocationContactWarning(outputContactIndex === -1);
          }
        }
      });
    }
  }, [data]);

  const checkAndAddAutoSaveWarning = useCallback(() => {
    if (isAutoSaveAllowed) {
      const proramName = data?.orderDetails?.name || '';
      setShowAutoSaveWarning(!proramName || proramName.length < 3);
    }
  }, [isAutoSaveAllowed, data]);

  const checkAndAddCloneProgramWarning = useCallback(() => {
    if (data?.metadata && data?.metadata.clonedFromProgram) {
      if (!data?.inputFile?.fileName) {
        setShowNoFileWarning(true);
      } else {
        if (data?.metadata.clonedFromProgram.fileName && draftId) {
          setShowFileWarning(
            data.inputFile.fileName ===
              data?.metadata.clonedFromProgram.fileName,
          );
        }
      }
    }
  }, [data, draftId]);

  const checkAndAddContactNotExistWarning = useCallback(() => {
    fetchClientAttributes();
  }, [fetchClientAttributes]);

  const checkAndAddSameContactWarning = useCallback(() => {
    if (data?.outputLocations && data?.outputLocations.length > 1) {
      const tempArray: string[] = [];
      let areSameContacts = false;
      (data?.outputLocations || []).forEach((element: OutputLocation) => {
        if (element.contact && element.contact?.name?.trim() !== '') {
          if (tempArray.indexOf(element.contact?.clientName) === -1) {
            tempArray.push(element.contact.clientName);
          } else {
            areSameContacts = true;
            setShowOutputContactName(element.contact.name);
            return false;
          }
        }
      });
      setShowOutputContactWarning(areSameContacts);
    }
  }, [data]);

  const addWarningsToMessageStore = useCallback(() => {
    checkAndAddAutoSaveWarning();
    checkAndAddCloneProgramWarning();
    checkAndAddContactNotExistWarning();
  }, [
    checkAndAddAutoSaveWarning,
    checkAndAddCloneProgramWarning,
    checkAndAddContactNotExistWarning,
  ]);

  useEffect(() => {
    addWarningsToMessageStore();
  }, [updateWarnings, addWarningsToMessageStore]);

  useEffect(() => {
    checkAndAddSameContactWarning();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.outputLocations]);

  useEffect(() => {
    if (showDeprecatedWarning) {
      const deprecateWarning = (messages || []).find(
        (msg) => msg.id === ProgramWizardMessageId.DeprecatedLookupsExist,
      );
      setDeprecatedAttributesWarning(
        deprecateWarning ? { ...deprecateWarning } : undefined,
      );
    }

    addWarningsToMessageStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, addWarningsToMessageStore]);

  return (
    <>
      {deprecatedAttributesWarning && showDeprecatedWarning && (
        <Modal
          isOpen={!!deprecatedAttributesWarning}
          header={
            <Flex alignItems='center' gap={2}>
              <Icon as={AiOutlineInfoCircle} h={5} w={5} color='orange.600' />
              Following fields are either deprecated or you do not have access
              to them
            </Flex>
          }
          onClose={() => {
            setDeprecatedAttributesWarning(undefined);
            setShowDeprecatedWarning(false);
          }}
          footer={
            <Flex gap={2}>
              <Button
                colorScheme='blue'
                onClick={() => {
                  setDeprecatedAttributesWarning(undefined);
                  setShowDeprecatedWarning(false);
                }}
              >
                OK
              </Button>
            </Flex>
          }
          showCloseButton={false}
          size={'6xl'}
        >
          {deprecatedAttributesWarning.message}
        </Modal>
      )}
      {(showFileWarning ||
        showOutputContactWarning ||
        showAutoSaveWarning ||
        showClientContactWarning ||
        showOutputLocationContactWarning) && (
        <>
          <Alert status='warning' variant='left-accent' mb={6}>
            <AlertIcon />
            <Box mx={3}>
              <ol>
                {showFileWarning && (
                  <li>
                    {`This program is using the previously uploaded program '`}{' '}
                    <b>{data?.metadata?.clonedFromProgram?.orderName}</b>
                    {`'. To update your consumer list, upload a new file in the Input File Selection tab before submitting the program.`}
                  </li>
                )}

                {showNoFileWarning && (
                  <li>
                    {`Input file is no longer available. Please upload/select a new file in the Input File Selection tab.`}
                  </li>
                )}

                {showOutputContactWarning && (
                  <li>
                    {`'`}
                    <b>{showOutputContactName}</b>
                    {`' has been selected for multiple output deliveries`}
                  </li>
                )}

                {showClientContactWarning && (
                  <li>
                    Client contact for order details is no longer available.
                    Please select client contact.
                  </li>
                )}

                {showOutputLocationContactWarning && (
                  <li>
                    Contact details for output location are no longer available.
                    Please select contact details for output location.
                  </li>
                )}

                {showAutoSaveWarning && (
                  <li>
                    Auto saving is paused. To continue auto saving this program,
                    please enter a valid program name.
                  </li>
                )}
              </ol>
            </Box>
          </Alert>
        </>
      )}
    </>
  );
};
